import { Banner } from "components/DesignSystem/Banner/Banner";
import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import { Tag } from "components/DesignSystem/Tag/Tag";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import ConditionalToolTip from "components/design/conditionalToolTip";
import { ReconciliationHeader } from "components/Reconciliation/ReconciliationTxn";
import { DISPLAY_DATE } from "constants/date";
import dayjs from "dayjs";
import { useCurrentChatGroupContext } from "hooks/useCurrentChatGroupContext";
import { useToast } from "hooks/useToast";
import {
  StripeReconcileDetail,
  StripeReconcileFinding,
  useCompleteReconciliationMutation,
} from "store/apis/reconciliation";

const RESULT_BANNER = {
  RECONCILED: {
    type: "success",
    title: "Books match the Stripe statement",
  },
  NEEDS_ATTENTION: {
    type: "warning",
    title: "Books match, but a few things need a look",
  },
  NOT_RECONCILED: {
    type: "error",
    title: "Books don't match the Stripe statement",
  },
} as const;

const SEVERITY_TAG = {
  ERROR: { tagType: "red", label: "Error" },
  WARNING: { tagType: "orange", label: "Warning" },
  INFO: { tagType: "gray", label: "Info" },
} as const;

const SEVERITY_ORDER = { ERROR: 0, WARNING: 1, INFO: 2 };

const BalanceRow = ({
  label,
  balances,
}: {
  label: string;
  balances?: Record<"CASH" | "ACCRUAL", string>;
}) => (
  <div className="t-flex t-justify-between t-text-body-sm">
    <span className="t-text-text-60">{label}</span>
    <span className="t-flex t-gap-4">
      <span>
        Cash <AmountSuperScript amount={Number(balances?.CASH ?? 0)} />
      </span>
      <span>
        Accrual <AmountSuperScript amount={Number(balances?.ACCRUAL ?? 0)} />
      </span>
    </span>
  </div>
);

const FindingRow = ({ finding }: { finding: StripeReconcileFinding }) => {
  const { tagType, label } = SEVERITY_TAG[finding.severity];
  return (
    <div className="t-flex t-gap-3 t-items-start t-py-3 t-border-0 t-border-b t-border-solid t-border-neutral-0 last:t-border-b-0">
      <span className="t-shrink-0">
        <Tag tagType={tagType} size="small" icon={false}>
          {label}
        </Tag>
      </span>
      <span className="t-text-body-sm t-text-text-100">{finding.message}</span>
    </div>
  );
};

export const StripeReconciliationModal = ({
  detail,
  isOpen,
  onClose,
  entityId,
}: {
  detail: StripeReconcileDetail;
  isOpen: boolean;
  onClose: () => void;
  entityId: string;
}) => {
  const { uuid: groupId } = useCurrentChatGroupContext();
  const { alertToast, successToast } = useToast();
  const [completeReconciliation, { isLoading }] =
    useCompleteReconciliationMutation();

  const banner = RESULT_BANNER[detail.result];
  // balances are not computed when nothing is synced for the period
  const hasBalances = Boolean(
    detail.metrics.stripe_balance || detail.metrics.merchant_processor_balance
  );
  const canConfirm = detail.result !== "NOT_RECONCILED";
  const periodEnd = dayjs(detail.reconcilation_end_date).format(DISPLAY_DATE);
  const findings = [...detail.findings].sort(
    (a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity]
  );

  const onConfirm = async () => {
    try {
      await completeReconciliation({
        groupId,
        entityId,
        reconcilationId: detail.reconciliation_state_id,
        unreconciled_transaction_ids: [],
        txn_ids_to_reconcile: [],
      }).unwrap();
      successToast({
        title: "Reconciliation completed successfully",
        message: `For ${detail.bank_name} till ${periodEnd}`,
      });
      onClose();
    } catch (error: any) {
      alertToast({ message: error?.data?.error?.message });
    }
  };

  return (
    <Modal.Root open={isOpen} onOpenChange={onClose}>
      <Modal.Content>
        <Modal.Header>
          <ReconciliationHeader
            brandName={detail.bank_name}
            accountName={detail.account_name}
          />
        </Modal.Header>
        <Modal.Body className="t-flex t-flex-col t-gap-4">
          <div className="t-text-body-sm t-text-text-60">
            {dayjs(detail.reconcilation_start_date).format(DISPLAY_DATE)} –{" "}
            {periodEnd} · Stripe ending balance{" "}
            <AmountSuperScript
              amount={Number(detail.customer_statement_ending_balance)}
            />
          </div>

          <Banner type={banner.type} title={banner.title} description="" />

          {hasBalances && (
            <div className="t-flex t-flex-col t-gap-2 t-p-3 t-rounded-lg t-bg-surface-lighter-grey">
              <BalanceRow
                label="Stripe balance in books"
                balances={detail.metrics.stripe_balance}
              />
              <BalanceRow
                label="Stripe Merchant Processor"
                balances={detail.metrics.merchant_processor_balance}
              />
            </div>
          )}

          <div>
            <div className="t-text-subtext t-mb-1">
              {findings.length ? "What we found" : "All checks passed"}
            </div>
            {findings.map((finding, index) => (
              <FindingRow key={`${finding.check}-${index}`} finding={finding} />
            ))}
          </div>
        </Modal.Body>
        <Modal.Footer className="t-flex t-justify-end t-gap-3">
          <Button onClick={onClose}>Close</Button>
          <ConditionalToolTip
            condition={
              !canConfirm &&
              "Fix the errors in the books, then upload the statement again"
            }
          >
            <span>
              <Button
                customType="primary"
                onClick={onConfirm}
                disabled={!canConfirm || isLoading}
                isLoading={isLoading}
              >
                Confirm reconciliation
              </Button>
            </span>
          </ConditionalToolTip>
        </Modal.Footer>
      </Modal.Content>
    </Modal.Root>
  );
};
